/**
 * Utility functions for log-scale time axis calculations.
 * Values are in MINUTES for internal calculations.
 */

// Time unit boundaries in minutes
const DAY = 60 * 24;

// Nice tick values in minutes for log scale
const NICE_TICKS_MINUTES = [
  0.5,      // 30s
  1,        // 1m
  2,        // 2m
  5,        // 5m
  10,       // 10m
  15,       // 15m
  30,       // 30m
  60,       // 1h
  120,      // 2h
  360,      // 6h
  720,      // 12h
  1440,     // 1d
  2880,     // 2d
  4320,     // 3d
  10080,    // 7d
  20160,    // 14d
  43200,    // 30d
];

/**
 * Format minutes into a human-readable time string with units.
 */
export function formatTimeWithUnit(minutes: number): string {
  if (minutes < 1) {
    const seconds = Math.round(minutes * 60);
    return `${seconds}s`;
  }
  if (minutes < 60) {
    const mins = Math.round(minutes);
    return `${mins}m`;
  }
  if (minutes < DAY) {
    const hours = minutes / 60;
    if (hours === Math.floor(hours)) {
      return `${Math.floor(hours)}h`;
    }
    return `${hours.toFixed(1)}h`;
  }
  const days = minutes / DAY;
  if (days === Math.floor(days)) {
    return `${Math.floor(days)}d`;
  }
  return `${days.toFixed(1)}d`;
}

/**
 * Compute the log-scale position (0-1) for a value within a domain.
 * Handles values at or near zero gracefully.
 */
export function logPosition(value: number, minVal: number, maxVal: number): number {
  if (value <= 0 || minVal <= 0 || maxVal <= 0) return 0;
  if (minVal === maxVal) return 0.5;

  const logMin = Math.log10(minVal);
  const logMax = Math.log10(maxVal);
  const logVal = Math.log10(value);

  return (logVal - logMin) / (logMax - logMin);
}

/**
 * Compute nice domain boundaries for log scale.
 * Floors the min and ceils the max to human-friendly values.
 */
export function computeNiceDomain(values: number[]): { min: number; max: number } {
  if (values.length === 0) {
    return { min: 1, max: 60 }; // Default: 1m to 1h
  }

  const actualMin = Math.min(...values);
  const actualMax = Math.max(...values);

  // Find nice floor for min
  let niceMin = NICE_TICKS_MINUTES[0];
  for (const tick of NICE_TICKS_MINUTES) {
    if (tick <= actualMin) {
      niceMin = tick;
    } else {
      break;
    }
  }
  // Ensure we go at least one step below the actual min
  const minIndex = NICE_TICKS_MINUTES.indexOf(niceMin);
  if (minIndex > 0 && niceMin === actualMin) {
    niceMin = NICE_TICKS_MINUTES[minIndex - 1];
  }

  // Find nice ceil for max
  let niceMax = NICE_TICKS_MINUTES[NICE_TICKS_MINUTES.length - 1];
  for (let i = NICE_TICKS_MINUTES.length - 1; i >= 0; i--) {
    if (NICE_TICKS_MINUTES[i] >= actualMax) {
      niceMax = NICE_TICKS_MINUTES[i];
    } else {
      break;
    }
  }
  // Ensure we go at least one step above the actual max
  const maxIndex = NICE_TICKS_MINUTES.indexOf(niceMax);
  if (maxIndex < NICE_TICKS_MINUTES.length - 1 && niceMax === actualMax) {
    niceMax = NICE_TICKS_MINUTES[maxIndex + 1];
  }

  // Add padding: expand domain slightly so edge dots aren't clipped
  const logMin = Math.log10(niceMin);
  const logMax = Math.log10(niceMax);
  const logRange = logMax - logMin;
  const paddingRatio = 0.05; // 5% padding on each side

  const paddedMin = Math.pow(10, logMin - logRange * paddingRatio);
  const paddedMax = Math.pow(10, logMax + logRange * paddingRatio);

  return { min: paddedMin, max: paddedMax };
}

/**
 * Generate nice tick values for a log scale domain.
 * Returns ticks that fall within the domain with human-friendly labels.
 * Limits to maxTicks to avoid crowding in narrow spaces.
 */
export function generateLogTicks(minVal: number, maxVal: number, maxTicks: number = 4): number[] {
  const ticks: number[] = [];

  for (const tick of NICE_TICKS_MINUTES) {
    if (tick >= minVal && tick <= maxVal) {
      ticks.push(tick);
    }
  }

  // Thin out ticks if we have too many
  while (ticks.length > maxTicks && ticks.length > 2) {
    // Remove every other tick, keeping first and last
    const thinned: number[] = [ticks[0]];
    for (let i = 2; i < ticks.length - 1; i += 2) {
      thinned.push(ticks[i]);
    }
    thinned.push(ticks[ticks.length - 1]);
    ticks.length = 0;
    ticks.push(...thinned);
  }

  // If we have too few ticks, we may need to add intermediate values
  if (ticks.length < 2 && minVal > 0 && maxVal > minVal) {
    // Add the min and max as ticks
    if (!ticks.includes(minVal)) ticks.unshift(minVal);
    if (!ticks.includes(maxVal)) ticks.push(maxVal);
  }

  return ticks;
}

/**
 * Generate a seeded pseudo-random number for consistent jitter.
 * This ensures the same incident always gets the same jitter value.
 */
export function seededRandom(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to 0-1 range
  return (Math.abs(hash) % 1000) / 1000;
}
